/**
 * KYC List Management - Minimal RTL Design
 *
 * یک صف کاری احراز هویت که هم درخواست‌های در انتظار و هم موارد تعیین‌تکلیف‌شده
 * (تایید/رد) را با تب‌های وضعیت + شمارش زنده، جستجو و ستون بررسی‌کننده نشان می‌دهد.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InboxIcon from '@mui/icons-material/Inbox';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import kycService from '../services/kyc.service';
import KycDetailModal from './KycDetailModal';

import './KycList.css';
import AssigneeCell from '../components/common/AssigneeCell';

const statusLabels: Record<string, string> = {
  pending: 'در حال بررسی',
  approved: 'تایید شده',
  rejected: 'رد شده'
};

type Counts = { all: number; pending: number; approved: number; rejected: number };

// تب‌ها: همه + سه وضعیت. مقدار '' یعنی همه‌ی وضعیت‌ها.
const TABS: { value: string; label: string; key: keyof Counts }[] = [
  { value: 'pending', label: 'در انتظار', key: 'pending' },
  { value: 'approved', label: 'تایید شده', key: 'approved' },
  { value: 'rejected', label: 'رد شده', key: 'rejected' },
  { value: '', label: 'همه', key: 'all' }
];

const KycList: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>('pending');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Counts>({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await kycService.list({ status, q: search, page: p, limit: 10 });
      setRows(res?.data?.items || []);
      setPages(res?.data?.pages || 1);
      setTotal(res?.data?.total || 0);
      setCounts(res?.data?.counts || { all: 0, pending: 0, approved: 0, rejected: 0 });
      setPage(p);
    } catch (e) {
      console.error('Error loading KYC list:', e);
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  // بارگذاری مجدد هنگام تغییر تب وضعیت یا عبارت جستجوی اعمال‌شده
  useEffect(() => {
    load(1);
  }, [load]);

  const submitSearch = () => setSearch(searchInput.trim());
  const clearSearch = () => { setSearchInput(''); setSearch(''); };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPagination = () => {
    const buttons = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(pages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        disabled={page <= 1}
        onClick={() => load(page - 1)}
      >
        <ChevronRightIcon fontSize="small" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${i === page ? 'active' : ''}`}
          onClick={() => load(i)}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        disabled={page >= pages}
        onClick={() => load(page + 1)}
      >
        <ChevronLeftIcon fontSize="small" />
      </button>
    );

    return buttons;
  };

  return (
    <div className="kyc-list-page">
      {/* Header */}
      <header className="kyc-header">
        <h1><VerifiedUserIcon /> مدیریت احراز هویت (KYC)</h1>
        <div className="header-actions">
          {loading && <CircularProgress size={20} />}
          <button className="header-btn refresh" onClick={() => load(page)} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </header>

      {/* Status tabs with live counts */}
      <div className="kyc-tabs">
        {TABS.map((t) => (
          <button
            key={t.value || 'all'}
            className={`kyc-tab ${status === t.value ? 'active' : ''} ${t.value || 'all'}`}
            onClick={() => { if (status !== t.value) setStatus(t.value); }}
          >
            <span className="kyc-tab-label">{t.label}</span>
            <span className="kyc-tab-count">{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="kyc-filters">
        <div className="kyc-search-box">
          <SearchIcon fontSize="small" />
          <input
            className="kyc-search-input"
            value={searchInput}
            placeholder="جستجو بر اساس شماره ملی یا نام..."
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
          />
          {searchInput && (
            <button className="kyc-search-clear" onClick={clearSearch} title="پاک کردن">×</button>
          )}
        </div>
        <button className="filter-btn" onClick={submitSearch}>
          <SearchIcon fontSize="small" />
          جستجو
        </button>
        <span className="kyc-result-count">{total.toLocaleString('fa-IR')} مورد</span>
      </div>

      {/* Table - Desktop */}
      <div className="kyc-table-wrapper">
        <table className="kyc-table">
          <thead>
            <tr>
              <th>نام</th>
              <th>ایمیل</th>
              <th>وضعیت</th>
              <th>مسئول</th>
              <th>شماره ملی</th>
              <th>تاریخ ارسال</th>
              <th>بررسی‌کننده</th>
              <th>تاریخ بررسی</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {loading && !rows.length ? (
              <tr>
                <td colSpan={9}>
                  <div className="loading-overlay"><CircularProgress size={30} /></div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="empty-state">
                    <InboxIcon />
                    <p>موردی یافت نشد</p>
                  </div>
                </td>
              </tr>
            ) : rows.map((r: any) => (
              <tr key={r._id}>
                <td>{r.user?.name || r.kycFullName || '-'}</td>
                <td>{r.user?.email || '-'}</td>
                <td>
                  <span className={`status-badge ${r.status}`}>
                    {statusLabels[r.status] || r.status}
                  </span>
                </td>
                <td><AssigneeCell value={r.assignedTo} /></td>
                <td>{r.nationalIdNumber || '-'}</td>
                <td>{formatDate(r.submittedAt)}</td>
                <td>{r.reviewedByUser?.name || (r.status === 'pending' ? '—' : '-')}</td>
                <td>{r.status === 'pending' ? '—' : formatDate(r.reviewedAt)}</td>
                <td>
                  <button
                    className="action-btn"
                    onClick={() => setDetailId(r._id)}
                  >
                    جزئیات
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="kyc-cards">
        {rows.length === 0 && !loading && (
          <div className="empty-state">
            <InboxIcon />
            <p>موردی یافت نشد</p>
          </div>
        )}
        {rows.map((r: any) => (
          <div key={r._id} className="kyc-card">
            <div className="kyc-card-header">
              <div>
                <div className="kyc-card-name">{r.user?.name || r.kycFullName || '-'}</div>
                <div className="kyc-card-email">{r.user?.email || '-'}</div>
              </div>
              <span className={`status-badge ${r.status}`}>
                {statusLabels[r.status] || r.status}
              </span>
            </div>
            <div className="kyc-card-row">
              <span className="kyc-card-label">شماره ملی:</span>
              <span className="kyc-card-value">{r.nationalIdNumber || '-'}</span>
            </div>
            <div className="kyc-card-row">
              <span className="kyc-card-label">تاریخ ارسال:</span>
              <span className="kyc-card-value">{formatDate(r.submittedAt)}</span>
            </div>
            {r.status !== 'pending' && (
              <div className="kyc-card-row">
                <span className="kyc-card-label">بررسی‌کننده:</span>
                <span className="kyc-card-value">{r.reviewedByUser?.name || '-'}</span>
              </div>
            )}
            <div className="kyc-card-actions">
              <button
                className="action-btn"
                style={{ width: '100%' }}
                onClick={() => setDetailId(r._id)}
              >
                مشاهده جزئیات
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="kyc-pagination">
          {renderPagination()}
        </div>
      )}

      {/* Detail modal (replaces navigating to /kyc/:id) */}
      <KycDetailModal
        open={!!detailId}
        id={detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => load(page)}
      />
    </div>
  );
};

export default KycList;
